import { Box, Flex, Tag, TagCloseButton, TagLabel, Text, useMediaQuery } from '@chakra-ui/react'
import React, { type Dispatch, type ReactElement, type SetStateAction } from 'react'

import CourseQ from '@/queries/CourseQ'
import TopicQ from '@/queries/TopicQ'
import type { Query } from '@/client/query'
import { getTopicUrlById } from '@/client/services/url'

import CourseFilters from '@/components/Course/CourseFilters/CourseFilters'
import { useQueryTopics, useUpdateSelectedTopics } from '@/components/Course/helpers'
import ShwLink from '@/components/ShwLink'
import { largeScreen } from '@/components/UI/common.styled'

export default function CourseResultsSingleHeader({
  course,
  disabled,
  setPageDisabled,
}: {
  course: Query
  disabled: boolean
  setPageDisabled: Dispatch<SetStateAction<boolean>>
}): ReactElement {
  const [isLgPlus] = useMediaQuery(`(min-width: ${largeScreen})`)

  const topics = course.many(CourseQ.getTopics())

  const topicsSelected = useQueryTopics()
  const singularSelectedTopic = topics.find(item => (item(TopicQ.id) ?? 0) === topicsSelected[0])
  const updateSelectedTopics = useUpdateSelectedTopics(setPageDisabled)

  const handleCloseTag = React.useCallback(
    (topicId: number) => {
      const topicsSelectedSet = new Set(topicsSelected)
      topicsSelectedSet.delete(topicId)
      updateSelectedTopics(Array.from(topicsSelectedSet))
    },
    [topicsSelected, updateSelectedTopics]
  )

  return (
    <Flex
      flexDir="column"
      position="sticky"
      top={topicsSelected.length === 0 ? 0 : '64px'}
      zIndex={1}
      pt={3}
      bgColor="bg"
    >
      {topicsSelected.length === 1 ? (
        <Box mb="16px">
          <Text>
            Results for{' '}
            <ShwLink
              variant="subtle"
              href={getTopicUrlById(singularSelectedTopic?.(TopicQ.id) ?? 0)}
            >
              {singularSelectedTopic?.(TopicQ.name) ?? ''}
            </ShwLink>
          </Text>
        </Box>
      ) : null}{' '}
      {topicsSelected.length > 1 ? (
        <Box mb="16px">
          <Text color="fg.muted">Results for {topicsSelected.length} Topics</Text>
        </Box>
      ) : null}
      {!isLgPlus ? (
        <CourseFilters
          course={course}
          isModal
          disabled={disabled}
          setPageDisabled={setPageDisabled}
        />
      ) : null}
      <Box>
        {topics.map(topic => {
          const topicId = topic(TopicQ.id) ?? 0
          const topicName = topic(TopicQ.name)
          if (!topicsSelected.includes(topicId ?? 0)) {
            return null
          }
          return (
            <Tag colorScheme="gray" mr={1} mb={1} key={topicId}>
              <TagLabel>{topicName}</TagLabel>
              <TagCloseButton
                onClick={() => handleCloseTag(topicId ?? 0)}
                _hover={{ cursor: 'pointer' }}
                isDisabled={disabled}
                value={topicId ?? 0}
                border="none"
              />
            </Tag>
          )
        })}
        {topicsSelected.length > 0 ? (
          <Tag
            mr={1}
            mb={1}
            color="primary.fg"
            backgroundColor="bg"
            fontWeight="bold"
            onClick={() => updateSelectedTopics([])}
            _hover={{
              cursor: 'pointer',
            }}
          >
            Reset Filters
          </Tag>
        ) : null}
      </Box>
    </Flex>
  )
}
